/**
 * Tarrat.tsx — tulostettava koetarra-arkki (54, Toni 5.10.2026 klo 8.21: «Lisää demoon koesarja (esim. demo 1–20) ja
 * tulostettava sivu niistä, jotta kamerakoe voidaan tehdä»).
 *
 * Jokainen tarra on QR-koodi, jonka sisältö on tämän instanssin oma osoite `<origin>/k/<n>` — sama reitti johon
 * sampo.pro/k/<asiakaskoodi>/<n> ohjaa (172). Demolla ei ole asiakaskoodia (koodi annetaan vasta kun tarroja
 * tilataan, deploy/asiakaskoodit.tsv sääntö 1), joten koetarra osoittaa suoraan instanssiin. Numerot on
 * tarkistettava sarjasta (qr_numeroalue): numero jota ei ole sarjassa ei sido mitään (181).
 *
 * QR-koodaus: qrcode-generator (MIT, Kazuhiko Arase) PAIKALLISENA tiedostona /qrcode.js, ladataan vasta tällä sivulla —
 * sama malli kuin jsqr (54). Ei npm-riippuvuutta.
 */
import { useEffect, useState } from "react";
import { api, type Kayttaja } from "./api";
import { t } from "./kieli";

declare global { interface Window { qrcode?: (typeNumber: number, errorCorrectionLevel: string) => { addData: (s: string) => void; make: () => void; createSvgTag: (o: { cellSize?: number; margin?: number; scalable?: boolean }) => string } } }

function lataaQrcode(): Promise<NonNullable<Window["qrcode"]>> {
  return new Promise((ok, ei) => {
    if (window.qrcode) return ok(window.qrcode);
    const s = document.createElement("script");
    s.src = "/qrcode.js"; s.async = true;
    s.onload = () => (window.qrcode ? ok(window.qrcode) : ei(new Error("qrcode.js latautui mutta qrcode puuttuu")));
    s.onerror = () => ei(new Error("qrcode.js ei latautunut"));
    document.head.appendChild(s);
  });
}

export default function Tarrat({ kayttaja: _k, alue }: { kayttaja: Kayttaja; alue: string }) {
  const m = /^(\d+)-(\d+)$/.exec(alue.trim());
  const alku = m ? Number(m[1]) : 1, loppu = m ? Math.min(Number(m[2]), Number(m[1]) + 199) : 20;
  const [svgt, setSvgt] = useState<Record<number, string> | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [sarjassa, setSarjassa] = useState<Record<number, boolean> | null>(null);
  const origin = window.location.origin;

  useEffect(() => {
    let elossa = true;
    lataaQrcode().then((qr) => {
      const out: Record<number, string> = {};
      for (let n = alku; n <= loppu; n++) {
        const q = qr(0, "M"); q.addData(`${origin}/k/${n}`); q.make();
        out[n] = q.createSvgTag({ cellSize: 3, margin: 2, scalable: true });
      }
      if (elossa) setSvgt(out);
    }).catch((e) => elossa && setVirhe(e instanceof Error ? e.message : t("muu.tarrat.qr_virhe")));
    // Sarjatarkistus: numero jota ei ole qr_numeroalue-sarjassa ei sido mitään (181) — tarra sanoo sen itse.
    Promise.all(Array.from({ length: loppu - alku + 1 }, (_, i) => alku + i).map(async (n) => {
      try { const q = await api.kalustoQr(n); return [n, q.tila !== "ei_sarjassa"] as const; }
      catch { return [n, false] as const; }
    })).then((r) => { if (elossa) setSarjassa(Object.fromEntries(r)); });
    return () => { elossa = false; };
  }, [alku, loppu, origin]);

  return (
    <div className="tarra-arkki" data-tarra-arkki={`${alku}-${loppu}`}>
      <div className="eiprint" style={{ margin: "0 0 12px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>{t("muu.tarrat.otsikko", { a: alku, b: loppu })}</h1>
        <button className="nappi nappi--paa" onClick={() => window.print()}>{t("muu.tarrat.tulosta")}</button>
        <a className="nappi nappi--kevyt" href="#/kalusto">{t("muu.tarrat.takaisin")}</a>
        <span className="hint">{t("muu.tarrat.selite", { o: origin })}</span>
      </div>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      <div className="tarra-ruudukko">
        {Array.from({ length: loppu - alku + 1 }, (_, i) => alku + i).map((n) => (
          <div key={n} className={"tarra" + (sarjassa && sarjassa[n] === false ? " tarra--ei-sarjassa" : "")} data-tarra={n}
               data-sarjassa={sarjassa ? String(sarjassa[n] ?? false) : "?"}>
            {svgt ? <div className="tarra__qr" dangerouslySetInnerHTML={{ __html: svgt[n] ?? "" }} /> : <div className="tarra__qr tarra__qr--odottaa" />}
            <div className="tarra__numero">{n}</div>
            <div className="tarra__osoite">{origin.replace(/^https?:\/\//, "")}/k/{n}</div>
            {sarjassa && sarjassa[n] === false && <div className="tarra__varoitus">{t("muu.tarrat.ei_sarjassa")}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
